// График временного ряда: одна или две серии, две оси Y, периоды, линия цели (ровная или ступенькой),
// подсказка при наведении.
// Данные готовит prepareChartData (prepare.ts); здесь только отрисовка Recharts.

import { useMemo, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Tabs } from '@/components/ui/Tabs'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, formatDayMonth, formatMonthYear, formatNumber } from '@/lib/format'
import { PERIODS, todayIso, type Period } from '@/lib/periods'

import { GOAL_KEY, axisTicks, prepareChartData, yAxisFormat, type ChartBand, type ChartGoal, type ChartSeries, type PreparedSeries } from './prepare'

export type { ChartBand, ChartGoal, ChartSeries, SeriesPoint } from './prepare'

const SERIES_COLORS = ['var(--accent)', 'var(--chart-2)'] as const
const DAY = 24 * 3600 * 1000

export type TimeSeriesChartProps = {
  series: ChartSeries[]
  /** Управляемый период; без него компонент хранит период сам. */
  period?: Period
  defaultPeriod?: Period
  onPeriodChange?: (period: Period) => void
  /** Какие периоды предлагать (по умолчанию все). */
  periods?: Period[]
  showPeriods?: boolean
  goal?: ChartGoal | null
  /** Коридор нормы: заливка между границами. */
  band?: ChartBand | null
  height?: number
  /** «Сегодня» как 'YYYY-MM-DD' — для тестов и дней в поясе пользователя. */
  today?: string
  emptyText?: string
  className?: string
}

type TooltipEntry = { dataKey?: unknown; value?: unknown }

function ChartTooltip({
  active,
  label,
  payload,
  series,
  withTime,
  goal,
}: {
  active?: boolean
  label?: unknown
  payload?: readonly TooltipEntry[]
  series: PreparedSeries[]
  withTime: boolean
  /** Ступенчатая цель: подпись и формат значения как у первой серии её оси. */
  goal: { label: string; unit?: string; digits?: number } | null
}) {
  if (!active || !payload?.length || typeof label !== 'number') return null
  const goalEntry = goal ? payload.find((p) => p.dataKey === GOAL_KEY) : undefined
  const goalValue = typeof goalEntry?.value === 'number' ? goalEntry.value : null
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 text-xs shadow-lg">
      <div className="mb-1 font-medium text-muted">{withTime ? formatDateTime(label) : formatDate(label)}</div>
      {series.map((s, index) => {
        const entry = payload.find((p) => p.dataKey === s.key)
        const value = typeof entry?.value === 'number' ? entry.value : null
        if (value === null) return null
        return (
          <div key={s.key} className="flex items-center gap-2 tabular-nums">
            <span className="h-2 w-2 rounded-full" style={{ background: SERIES_COLORS[index] }} />
            <span className="text-muted">{s.label}</span>
            <span className="ml-auto pl-3 font-semibold text-fg">
              {formatNumber(value, { digits: s.digits ?? 1 })}
              {s.unit ? ` ${s.unit}` : ''}
            </span>
          </div>
        )
      })}
      {goal && goalValue !== null ? (
        <div className="flex items-center gap-2 tabular-nums">
          <span className="h-0.5 w-2 rounded" style={{ background: 'var(--accent)', opacity: 0.7 }} />
          <span className="text-muted">{goal.label}</span>
          <span className="ml-auto pl-3 text-fg">
            {formatNumber(goalValue, { digits: goal.digits ?? 1 })}
            {goal.unit ? ` ${goal.unit}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  )
}

export function TimeSeriesChart({
  series,
  period: controlled,
  defaultPeriod = 'month',
  onPeriodChange,
  periods,
  showPeriods = true,
  goal,
  band,
  height = 220,
  today,
  emptyText = 'Нет данных за этот период',
  className,
}: TimeSeriesChartProps) {
  const [own, setOwn] = useState<Period>(defaultPeriod)
  const period = controlled ?? own
  const day = today ?? todayIso()
  const data = useMemo(
    () => prepareChartData({ series, period, today: day, goal, band }),
    [series, period, day, goal, band],
  )

  const choose = (next: Period) => {
    if (controlled === undefined) setOwn(next)
    onPeriodChange?.(next)
  }

  const spanDays = (data.xDomain[1] - data.xDomain[0]) / DAY
  const withTime = data.rows.some((r) => new Date(r.t).getHours() !== 0 || new Date(r.t).getMinutes() !== 0)
  const tickX = (t: number) => (spanDays > 92 ? formatMonthYear(t) : formatDayMonth(t))
  const leftY = yAxisFormat(data.axes.left.domain, 44)
  const rightY = data.axes.right ? yAxisFormat(data.axes.right.domain, 40) : null
  const manyPoints = data.rows.length > 45
  const items = PERIODS.filter((p) => !periods || periods.includes(p.value))
  const goalLabel = goal?.label ?? 'Цель'
  const goalSeries = data.series.find((s) => s.axis === (goal?.axis ?? 'left'))
  const tooltipGoal = data.steppedGoal
    ? { label: goalLabel, unit: goalSeries?.unit, digits: goalSeries?.digits }
    : null

  return (
    <div className={cn('min-w-0', className)}>
      {showPeriods ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <Tabs items={items} value={period} onChange={choose} label="Период" size="sm" />
          {data.series.length > 1 || data.steppedGoal ? (
            <div className="flex flex-wrap gap-3 text-xs text-muted">
              {data.series.map((s, index) => (
                <span key={s.key} className="inline-flex items-center gap-1.5">
                  <span className="h-0.5 w-3 rounded" style={{ background: SERIES_COLORS[index] }} />
                  {s.label}
                  {s.unit ? `, ${s.unit}` : ''}
                </span>
              ))}
              {data.steppedGoal ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 border-t border-dashed border-accent opacity-70" />
                  {goalLabel}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {data.hasData ? (
        <div style={{ height }} className="w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.rows} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="t"
                type="number"
                scale="time"
                domain={data.xDomain}
                ticks={axisTicks(data.xDomain)}
                tickFormatter={tickX}
                tick={{ fill: 'var(--muted)', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                minTickGap={24}
              />
              <YAxis
                yAxisId="left"
                domain={data.axes.left.domain}
                tickFormatter={leftY.format}
                tick={{ fill: 'var(--muted)', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={leftY.width}
                allowDataOverflow
              />
              {data.axes.right ? (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={data.axes.right.domain}
                  tickFormatter={rightY?.format}
                  tick={{ fill: 'var(--muted)', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={rightY?.width}
                  allowDataOverflow
                />
              ) : null}
              <Tooltip
                cursor={{ stroke: 'var(--muted)', strokeDasharray: '3 3' }}
                content={(props) => (
                  <ChartTooltip
                    active={props.active}
                    label={props.label}
                    payload={props.payload as readonly TooltipEntry[] | undefined}
                    series={data.series}
                    withTime={withTime}
                    goal={tooltipGoal}
                  />
                )}
              />
              {band ? (
                <ReferenceArea
                  yAxisId={band.axis ?? 'left'}
                  y1={band.low}
                  y2={band.high}
                  fill="var(--accent)"
                  fillOpacity={0.12}
                  stroke="none"
                  ifOverflow="hidden"
                  label={
                    band.label
                      ? { value: band.label, position: 'insideTopLeft', fill: 'var(--muted)', fontSize: 11 }
                      : undefined
                  }
                />
              ) : null}
              {goal && data.steppedGoal ? (
                <Line
                  yAxisId={goal.axis ?? 'left'}
                  dataKey={GOAL_KEY}
                  name={goalLabel}
                  type="stepAfter"
                  stroke="var(--accent)"
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  strokeOpacity={0.7}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ) : goal?.value !== undefined ? (
                <ReferenceLine
                  yAxisId={goal.axis ?? 'left'}
                  y={goal.value}
                  stroke="var(--accent)"
                  strokeDasharray="5 4"
                  strokeOpacity={0.7}
                  label={{
                    value: goalLabel,
                    position: 'insideTopRight',
                    fill: 'var(--muted)',
                    fontSize: 11,
                  }}
                />
              ) : null}
              {data.series.map((s, index) => (
                <Line
                  key={s.key}
                  yAxisId={s.axis}
                  dataKey={s.key}
                  name={s.label}
                  type="monotone"
                  stroke={SERIES_COLORS[index] ?? 'var(--muted)'}
                  strokeWidth={index === 0 ? 2 : 1.5}
                  strokeDasharray={index === 0 ? undefined : '4 3'}
                  dot={manyPoints ? false : { r: 2.5, strokeWidth: 0, fill: SERIES_COLORS[index] }}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div
          style={{ height }}
          className="flex items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted"
        >
          {emptyText}
        </div>
      )}
    </div>
  )
}
